import type { DashboardField } from "../types/contract";
import { coversThrough } from "./fertSets";

// Row "Action Flags": a red mark per action that HASN'T been done for a field.
export interface ActionFlag {
  key: string;
  label: string;
}

// nutrient-flag order + display names. Each key is matched against a SavedRecSet's `nutrients`.
export const RX_NUTRIENTS: { key: string; label: string }[] = [
  { key: "lime", label: "Lime" },
  { key: "p2o5", label: "Phosphorus" },
  { key: "k2o", label: "Potassium" },
];

/*
 * What coverage judging needs. A saved set satisfies it, and so does one row of
 * the org coverage read (a single nutrient and the year it covers through).
 * `null` means that read has not succeeded: nutrient flags stay off. The
 * soil-map flag does not wait on it.
 */
export interface RxCoverageLike {
  nutrients?: string[];
  coversThroughYear?: number;
  createdAt?: string;
}

export function computeActionFlags(
  field: DashboardField,
  coverage: RxCoverageLike[] | null,
  currentYear: number,
): ActionFlag[] {
  const flags: ActionFlag[] = [];
  // Prefer the server-computed flag (it also catches a map built from old samples).
  // Until that column is present, fall back to "the field has no soil map at all"
  // via `has_ml_map`, so a fresh field with no map still flags. `??` respects an
  // explicit server `false` (the map is current). Sample age stays on the server.
  const needsSoilMap = field.needs_updated_soil_map ?? !field.has_ml_map;
  if (needsSoilMap)
    flags.push({ key: "soil-map", label: "Needs Updated Soil Map" });
  if (coverage == null) return flags;
  for (const n of RX_NUTRIENTS) {
    const covered = coverage.some(
      (s) =>
        (s.nutrients ?? []).includes(n.key) && coversThrough(s) >= currentYear,
    );
    if (!covered)
      flags.push({
        key: `rx-${n.key}`,
        label: `Needs ${currentYear} ${n.label} Rx`,
      });
  }
  return flags;
}
